import { Mark } from "@mui/base";
import { Slider, SliderProps } from "@mui/material";
import { VALUE_TYPE } from "Components/NumberValue";
import {
  MAX_SUM_DAILY_PAYOUT_AMOUNT,
  MIN_SUM_DAILY_PAYOUT_AMOUNT,
} from "Features/CheckPrice/Products/LifeProducts/SopSicknessLeave/sopSicknessLeaveConstants";
import { SopSicknessLeavePriceRequestInfo } from "Features/CheckPrice/Products/LifeProducts/SopSicknessLeave/sopSicknessLeaveInterfaces";
import { formatToLocaleString } from "Formatting/formatNumber";
import { FormikProps } from "formik";
import React from "react";
import { isDefined } from "Utils/functions";

const sliderStyle: Required<SliderProps["sx"]> = {
  color: `var(--Selection, #5B462B)`,
  height: 8,
  padding: "13px 0",
  width: "90%",
  "& .MuiSlider-thumb": {
    height: 28,
    width: 28,
    backgroundColor: `var(--color-selection)`,
    backgroundImage: `url('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="8 8 32 32" fill="none"%3E%3Ccircle cx="24" cy="24" r="16" fill="white"/%3E%3Ccircle cx="24" cy="24" r="15.5" stroke="%23191919" stroke-opacity="0.16"/%3E%3Ccircle cx="24" cy="24" r="8" fill="%235B462B"/%3E%3C/svg%3E%0A')`,
  },
  "& .MuiSlider-active": {},
  "& .MuiSlider-valueLabel": {
    background: "transparent",
    color: "#bf0900",
    fontSize: "18px",
    fontWeight: "600",
    top: 0,
    width: "max-content",
  },
  "& .MuiSlider-rail": {
    color: `var(--unselected, #DDD)`,
    opacity: 1,
    height: 8,
    borderRadius: 8,
  },
  "& .MuiSlider-mark": {
    display: "none",
  },
  "& .MuiSlider-markLabel": {
    color: "#000000",
    '&[data-index="0"]': {
      transform: "translateX(0)",
    },
    '&[data-index="1"]': {
      transform: "translateX(-100%)",
    },
    '&[data-index="3"]': {
      transform: "translateX(-100%)",
    },
  },
};

interface Props {
  setCurrentSliderValue: (value: number) => void;
  handleCustomerChange: (changeValue: number | boolean) => void;
  step: number | null;
  min: number;
  minAdvice: number | null;
  maxAdvice: number | null;
  max: number;
  adviceValue: number;
  defaultValue: number;
  adviceValueMarkLabel: string;
  valueType: VALUE_TYPE;
  disabled?: boolean;
  label: string;
}

const InternalSlider = ({
  setCurrentSliderValue,
  handleCustomerChange,
  step,
  min,
  minAdvice,
  maxAdvice,
  max,
  defaultValue,
  valueType,
  disabled,
  label,
}: Props): React.ReactElement => {
  const trackLeft = (): number => {
    const value = (((minAdvice ?? min) - min) * 100) / (max - min);
    return Math.round(value);
  };

  const trackWidth = (): number => {
    const value = (((maxAdvice ?? min) - min) * 100) / (max - min);
    return Math.round(value) - trackLeft();
  };

  const sx: Required<SliderProps["sx"]> = {
    ...sliderStyle,
    "& .MuiSlider-track": {
      height: 8,
      left: isDefined(minAdvice) ? `${trackLeft()}% !important` : "auto",
      width: isDefined(maxAdvice) ? `${trackWidth()}% !important` : "auto",
    },
  };

  function createMarks(): Mark[] {
    const marks: Mark[] = [
      {
        value: min,
        label: `${formatToLocaleString(min)} ${valueType}`,
      },
    ];
    if (isDefined(minAdvice) && isDefined(maxAdvice)) {
      marks.push(...[{ value: minAdvice }, { value: maxAdvice }]);
    }
    marks.push({
      value: max,
      label: `${formatToLocaleString(max)} ${valueType}`,
    });
    return marks;
  }

  const marks = createMarks();

  return (
    <Slider
      sx={sx}
      value={defaultValue}
      onChange={(_, value) => setCurrentSliderValue(value as number)}
      onChangeCommitted={(_, value) => handleCustomerChange(value as number)}
      disabled={disabled}
      aria-label={label}
      step={step}
      marks={marks}
      min={min}
      max={max}
    />
  );
};

export const SopSlider = (props: {
  props: FormikProps<SopSicknessLeavePriceRequestInfo>;
  currentSliderValue: (value: number) => void;
  handleCustomerChange: (changeValue: number | boolean) => void;
}): React.ReactElement => {
  return (
    <InternalSlider
      adviceValue={props.props.getFieldProps("dailyPayoutAmount").value}
      adviceValueMarkLabel={""}
      valueType={VALUE_TYPE.NOK}
      label={"label"}
      maxAdvice={null}
      minAdvice={null}
      max={MAX_SUM_DAILY_PAYOUT_AMOUNT}
      min={MIN_SUM_DAILY_PAYOUT_AMOUNT}
      setCurrentSliderValue={props.currentSliderValue}
      handleCustomerChange={props.handleCustomerChange}
      step={50}
      defaultValue={Number(
        props.props.getFieldProps("dailyPayoutAmount").value,
      )}
    />
  );
};
